import React, { useState, useRef, useEffect } from 'react';
import { Clock, Layout, CheckCircle2, ChevronDown, Sparkles } from 'lucide-react';

interface SubHeaderTaskViewControlsProps {
  viewMode: string;
  setViewMode: (mode: any) => void;
  kanbanMode: 'status' | 'date' | 'custom';
  setKanbanMode: (mode: 'status' | 'date' | 'custom') => void;
  uiMode?: 'standard' | 'expert';
  onToggleUiMode?: () => void;
  onPrefetch?: (view: string) => void;
}

export const SubHeaderTaskViewControls: React.FC<SubHeaderTaskViewControlsProps> = ({
  viewMode,
  setViewMode,
  kanbanMode,
  setKanbanMode,
  uiMode = 'standard',
  onToggleUiMode,
  onPrefetch
}) => {
  const [showMoreViewsDropdown, setShowMoreViewsDropdown] = useState(false);
  const moreViewsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreViewsDropdownRef.current && !moreViewsDropdownRef.current.contains(event.target as Node)) {
        setShowMoreViewsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const kanbanModes = [
    { id: 'date', icon: <Clock size={14} />, label: 'Zeit & Wartend' },
    { id: 'custom', icon: <Layout size={14} />, label: 'Eigene Spalten' }
  ] as const;

  const standardTabs = [
    { id: 'kanban', label: 'Kanban' },
    { id: 'gantt', label: 'Gantt' },
    { id: 'list', label: 'Liste' },
    { id: 'calendar', label: 'Kalender' },
    { id: 'projectContact', label: 'Kontakte' },
    ...(['timeline', 'mindmap', 'projectDocuments'].includes(viewMode) ? [
      { id: 'timeline', label: 'Zeitstrahl' },
      { id: 'mindmap', label: 'Mindmap' },
      { id: 'projectDocuments', label: 'Dokumente' }
    ].filter(t => t.id === viewMode) : [])
  ];

  const expertTabs = [
    { id: 'kanban', label: 'Kanban' },
    { id: 'gantt', label: 'Gantt' },
    { id: 'list', label: 'Liste' },
    { id: 'timeline', label: 'Zeitstrahl' },
    { id: 'mindmap', label: 'Mindmap' },
    { id: 'calendar', label: 'Kalender' },
    { id: 'projectContact', label: 'Kontakte' },
    { id: 'projectDocuments', label: 'Dokumente' }
  ];

  const tabs = uiMode === 'standard' ? standardTabs : expertTabs;

  return (
    <div className="flex items-center justify-center gap-4">
      {/* Kanban Mode Switcher - Space is reserved to prevent jumping */}
      <div className={`flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center shadow-inner gap-0.5 transition-all duration-300 ${
        viewMode === 'kanban' 
          ? 'opacity-100 translate-x-0 pointer-events-auto' 
          : 'opacity-0 -translate-x-2 pointer-events-none'
      }`}>
        {kanbanModes.map((mode) => (
          <button 
            key={mode.id}
            type="button"
            onClick={() => setKanbanMode(mode.id)} 
            className={`px-3 h-full rounded-lg flex items-center justify-center transition-all ${
              kanbanMode === mode.id 
                ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 ring-1 ring-black/5' 
                : 'text-slate-400 hover:text-slate-500 hover:bg-white/50'
            }`}
            title={mode.label}
          >
            {mode.icon}
          </button>
        ))}
      </div>

      {/* View Selector Tabs */}
      <div data-tour="view-switcher" className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl shadow-inner border border-slate-200/50 dark:border-slate-800 h-[34px] items-center gap-0.5 relative">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              React.startTransition(() => {
                setViewMode(tab.id as any);
              });
            }}
            onMouseEnter={() => onPrefetch?.(tab.id)}
            translate="no"
            className={`px-3.5 h-full rounded-lg text-[11px] font-bold uppercase tracking-[0.08em] transition-all whitespace-nowrap ${
              viewMode === tab.id
                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-black/5' 
                : 'text-slate-400 hover:text-slate-500'
            }`}
          >
            {tab.label}
          </button>
        ))}

        {/* More / Erweitert dropdown in Standard mode */}
        {uiMode === 'standard' && (
          <div className="relative" ref={moreViewsDropdownRef}>
            <button
              type="button"
              onClick={() => setShowMoreViewsDropdown(prev => !prev)}
              className={`px-2 h-full rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 ${
                ['timeline', 'mindmap', 'projectDocuments'].includes(viewMode)
                  ? 'text-indigo-600 dark:text-indigo-400 font-black'
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
              title="Weitere Ansichten (Zeitstrahl, Mindmap, Dokumente)"
            >
              <span>Mehr</span>
              <ChevronDown size={11} className={`transition-transform duration-200 ${showMoreViewsDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showMoreViewsDropdown && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-1.5 z-[150] space-y-0.5">
                {[
                  { id: 'timeline', label: 'Zeitstrahl', desc: 'Horizontale Projekt-Roadmap' },
                  { id: 'mindmap', label: 'Mindmap', desc: 'Strukturierte Gedankenkarte' },
                  { id: 'projectDocuments', label: 'Dokumente', desc: 'Dateien & Anhänge' }
                ].map(ext => (
                  <button
                    key={ext.id}
                    type="button"
                    onClick={() => {
                      React.startTransition(() => {
                        setViewMode(ext.id as any);
                      });
                      setShowMoreViewsDropdown(false);
                    }}
                    className={`w-full flex flex-col text-left px-3 py-2 rounded-xl text-xs transition-colors ${
                      viewMode === ext.id
                        ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 font-bold'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <span className="font-bold text-[11px] uppercase tracking-wider">{ext.label}</span>
                    <span className="text-[10px] text-slate-400 font-normal">{ext.desc}</span>
                  </button>
                ))}
                {onToggleUiMode && (
                  <div className="pt-1 mt-1 border-t border-slate-100 dark:border-slate-800">
                    <button
                      type="button"
                      onClick={() => {
                        onToggleUiMode();
                        setShowMoreViewsDropdown(false);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30 transition-colors"
                    >
                      <span>Alle Ansichten dauerhaft einblenden</span>
                      <Sparkles size={12} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
